import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { KeyRound, Loader2, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import api from '../lib/api';

const LOGO_URL = process.env.REACT_APP_LOGO_URL;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Ungültiger Reset-Link. Bitte fordere einen neuen an.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await api.post('/api/auth/reset-password', {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Passwort wurde erfolgreich zurückgesetzt!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      const message = err.response?.data?.detail || 'Ein Fehler ist aufgetreten';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 hex-pattern">
      <Link to="/login" className="absolute top-6 left-6 flex items-center gap-2 text-tunis-blue hover:text-tunis-teal transition-colors">
        <ArrowLeft className="w-5 h-5" />
        Zurück zum Login
      </Link>
      
      <div className="w-full max-w-md space-y-6 animate-fade-in">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-block">
            <img src={LOGO_URL} alt="Tunis Kick" className="h-20 w-20 mx-auto rounded-full shadow-lg" />
          </Link>
          <h1 className="mt-4 font-display font-bold text-2xl text-tunis-blue">Neues Passwort erstellen</h1>
          <p className="text-muted-foreground">Wähle ein sicheres Passwort</p>
        </div>

        <Card className="border-slate-200 shadow-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-tunis-teal" />
              Passwort zurücksetzen
            </CardTitle>
            <CardDescription>
              Gib dein neues Passwort ein. Es muss mindestens 6 Zeichen lang sein.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && !success && (
              <div className="text-center space-y-4 py-4">
                <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-red-600">Fehler</h3>
                  <p className="text-sm text-muted-foreground mt-2">{error}</p>
                </div>
                <Link to="/forgot-password">
                  <Button variant="outline" className="mt-4">
                    Neuen Reset-Link anfordern
                  </Button>
                </Link>
              </div>
            )}
            
            {success ? (
              <div className="text-center space-y-4 py-4">
                <div className="w-16 h-16 mx-auto bg-tunis-teal/10 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-tunis-teal" />
                </div>
                <div>
                  <h3 className="font-semibold text-tunis-blue">Passwort geändert!</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Dein Passwort wurde erfolgreich zurückgesetzt. Du wirst in Kürze zum Login weitergeleitet.
                  </p>
                </div>
                <Link to="/login">
                  <Button className="btn-primary mt-4">
                    Jetzt anmelden
                  </Button>
                </Link>
              </div>
            ) : !error && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Neues Passwort</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Mindestens 6 Zeichen"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="input-new-password"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Passwort wiederholen"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="input-confirm-password"
                    className="h-12"
                  />
                </div>
                {password && confirmPassword && password !== confirmPassword && (
                  <p className="text-sm text-red-500">Passwörter stimmen nicht überein</p>
                )}
                <Button
                  type="submit"
                  className="w-full btn-primary h-12"
                  disabled={loading || password !== confirmPassword}
                  data-testid="btn-reset-password"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Passwort speichern'
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
